import { Button } from './ui/button';
import { List, GitBranch, BarChart3, LayoutDashboard, Lightbulb, FileText, BookOpen, Network, LucideIcon } from 'lucide-react';

export type ViewMode = 'table' | 'tree';
export type AnalyticsViewMode = 'charts' | 'dashboards';
export type ContextViewMode = 'analytics' | 'company';
export type SemanticLayerViewMode = 'glossary' | 'diagram';

interface ToggleOption<T extends string> {
  value: T;
  label: string;
  icon: LucideIcon;
}

interface GenericToggleProps<T extends string> {
  value: T;
  onChange: (mode: T) => void;
  options: [ToggleOption<T>, ToggleOption<T>];
}

export function GenericToggle<T extends string>({ value, onChange, options }: GenericToggleProps<T>) {
  return (
    <div className="flex items-center gap-0.5 border rounded-md p-1">
      {options.map((option) => (
        <Button
          key={option.value}
          variant={value === option.value ? 'default' : 'ghost'}
          size="sm"
          onClick={() => onChange(option.value)}
          className="gap-2 h-8"
        >
          <option.icon className="h-4 w-4" />
          {option.label}
        </Button>
      ))}
    </div>
  );
}

interface ViewModeToggleProps {
  value: ViewMode;
  onChange: (mode: ViewMode) => void;
}

export function ViewModeToggle({ value, onChange }: ViewModeToggleProps) {
  const options: [ToggleOption<ViewMode>, ToggleOption<ViewMode>] = [
    { value: 'table', label: 'Table', icon: List },
    { value: 'tree', label: 'Tree', icon: GitBranch },
  ];
  return <GenericToggle value={value} onChange={onChange} options={options} />;
}

interface AnalyticsViewToggleProps {
  value: AnalyticsViewMode;
  onChange: (mode: AnalyticsViewMode) => void;
}

export function AnalyticsViewToggle({ value, onChange }: AnalyticsViewToggleProps) {
  const options: [ToggleOption<AnalyticsViewMode>, ToggleOption<AnalyticsViewMode>] = [
    { value: 'charts', label: 'Charts', icon: BarChart3 },
    { value: 'dashboards', label: 'Dashboards', icon: LayoutDashboard },
  ];
  return <GenericToggle value={value} onChange={onChange} options={options} />;
}

interface ContextViewToggleProps {
  value: ContextViewMode;
  onChange: (mode: ContextViewMode) => void;
}

export function ContextViewToggle({ value, onChange }: ContextViewToggleProps) {
  const options: [ToggleOption<ContextViewMode>, ToggleOption<ContextViewMode>] = [
    { value: 'analytics', label: 'Analytics Context', icon: Lightbulb },
    { value: 'company', label: 'Company Context', icon: FileText },
  ];
  return <GenericToggle value={value} onChange={onChange} options={options} />;
}

interface SemanticLayerViewToggleProps {
  value: SemanticLayerViewMode;
  onChange: (mode: SemanticLayerViewMode) => void;
}

export function SemanticLayerViewToggle({ value, onChange }: SemanticLayerViewToggleProps) {
  const options: [ToggleOption<SemanticLayerViewMode>, ToggleOption<SemanticLayerViewMode>] = [
    { value: 'glossary', label: 'Glossary', icon: BookOpen },
    { value: 'diagram', label: 'Diagram', icon: Network },
  ];
  return <GenericToggle value={value} onChange={onChange} options={options} />;
}
